/**
 * The brand everything agent-facing is currently about.
 *
 * Beatly itself has no global brand — a user works across all of them, and the
 * only brand state in their code is a per-page list filter. So a sticky
 * selection can never be the last word: when the page is about a specific
 * object, that object's brand wins, and the header says which one is live.
 * `useActiveBrand` is the sticky choice; `useEffectiveBrand` is what is
 * actually in force, and the agents are sent the latter.
 *
 * The cowork module kept this in CoworkPage's local state, which worked when
 * that page owned the whole screen. Inside the Beatly shell the Brand OS page,
 * the agent rail and the platform pages are siblings, so the selection has to
 * live above all of them — otherwise the rail can be talking about one brand
 * while Brand OS edits another.
 *
 * Storage key is shared with the standalone app on purpose: same user, same
 * choice, whichever surface they picked it in.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { parseAppPath } from '@/routes';
import { useQuery } from '@tanstack/react-query';
import { coworkApi } from '@/cowork/omniad/api/coworkApi';
import { isCoworkLive } from '@/cowork/live/coworkClient';

const STORAGE_KEY = 'cowork_active_brand_id';
const CHANGE_EVENT = 'cowork-active-brand-changed';

export function setActiveBrandId(brandId: string): void {
  localStorage.setItem(STORAGE_KEY, brandId);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: brandId }));
}

export function readActiveBrandId(): string | undefined {
  return localStorage.getItem(STORAGE_KEY) || undefined;
}

export function useActiveBrand() {
  const [brandId, setBrandIdState] = useState<string | undefined>(readActiveBrandId);

  // Keep every consumer of the hook in step, including across tabs.
  useEffect(() => {
    const onChange = () => setBrandIdState(readActiveBrandId());
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const { data } = useQuery({
    queryKey: ['cowork', 'brands'],
    queryFn: () => coworkApi.brands.list(),
    enabled: isCoworkLive(),
    staleTime: 5 * 60_000,
  });

  const brands = data?.brands ?? [];

  // Nothing chosen yet, or a stale id from a team the user left. Fall back to
  // the brand of the most recently created campaign — what the team is actually
  // working on — rather than whichever brand happened to sort first.
  const resolvedId =
    brandId && brands.some((b) => b.brandId === brandId)
      ? brandId
      : data?.defaultBrandId ?? brands[0]?.brandId ?? brandId;

  const select = useCallback((id: string) => {
    setActiveBrandId(id);
    setBrandIdState(id);
  }, []);

  return {
    brandId: resolvedId,
    brands,
    activeBrand: brands.find((b) => b.brandId === resolvedId),
    selectBrand: select,
  };
}

/**
 * The brand actually in force right now.
 *
 * On a campaign the campaign decides it — that is what the backend does too, so
 * a header showing anything else would be lying. Everywhere else it is the
 * user's last choice, which is the only signal available.
 */
export function useEffectiveBrand() {
  const { brandId: stickyId, brands, selectBrand } = useActiveBrand();
  const location = useLocation();
  const route = useMemo(() => parseAppPath(location.pathname), [location.pathname]);
  const campaignId = route.campaignId;

  const { data: campaignBrand } = useQuery({
    queryKey: ['cowork', 'campaign-brand', campaignId],
    queryFn: () => coworkApi.campaignWork.brand(campaignId as string),
    enabled: isCoworkLive() && Boolean(campaignId),
    staleTime: 5 * 60_000,
  });

  const derived = campaignBrand?.brandId ? campaignBrand : null;
  const sticky = brands.find((b) => b.brandId === stickyId);

  return {
    /** Brand in force — derived beats sticky. */
    brandId: derived?.brandId ?? stickyId,
    brandName: derived?.brandName ?? sticky?.brandName,
    brandLogoUrl: derived?.brandLogoUrl ?? sticky?.brandLogoUrl,
    /** True when the page dictates the brand, so switching here would be a lie. */
    isDerived: Boolean(derived),
    /** Where it comes from, for the tooltip. */
    derivedFrom: derived ? 'campaign' : null,
    brands,
    selectBrand,
  };
}
